import { useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, Server, ShieldCheck } from 'lucide-react';
import { apiService, PackContainer, PremiumData } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import styles from '@/pages/GradesPage.module.scss';

const NUM_FIELDS: [string, string][] = [
  ['LootCount', 'Nombre de loots (lootbox)'],
  ['IdleRewardSecond', 'Idle reward (s)'],
  ['ReducerLoseExperience', 'Réduction perte XP mort (%)'],
  ['AmountEnderChestLockedSlot', 'Slots enderchest verrouillés'],
  ['ViewDistance', 'Distance de vue'],
  ['RtpDailyLimit', 'Limite RTP / jour'],
  ['BonusXpJobsPercent', 'Bonus XP jobs (%)'],
  ['ExpirationTimeDeathLocate', 'Death locate (ms)'],
  ['MaxCanFeedDistance', 'Distance max feed'],
];
const LIST_FIELDS: [string, string, string][] = [
  ['RewardIds', 'Reward IDs (weekly)', 'ex. divinite_key, paladin_key'],
  ['Kits', 'Kits', 'ex. divinite_kit, legende_kit'],
];
const BOOL_FIELDS: [string, string][] = [
  ['CanFeedOtherPlayers', 'Peut nourrir les autres'],
  ['CanFeedSelf', 'Peut se nourrir'],
  ['HasFeedDistanceLimit', 'Limite de distance de feed'],
];

/** Onglet Boutique d'un grade : config premium (kits, rewards, RTP, loots…) par container.
 * Enregistrement via la barre du bas ; « Appliquer à tous » reste une action directe. */
export function GradeBoutique({ grade, readOnly = false }: { grade: string; readOnly?: boolean }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [containers, setContainers] = useState<PackContainer[]>([]);
  const [containerId, setContainerId] = useState('');
  const [data, setData] = useState<PremiumData | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [bools, setBools] = useState<Record<string, boolean>>({});
  const [orig, setOrig] = useState('');
  const [applyingAll, setApplyingAll] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    apiService.getContainers().then((r) => {
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
      setContainers(list);
      setContainerId((prev) => (list.some((c) => c.id === prev) ? prev : list[0]?.id ?? ''));
    });
  }, [activeGameId]);

  useEffect(() => {
    if (!containerId) { setData(null); return; }
    let active = true;
    setLoading(true);
    setFeedback(null);
    apiService.getContainerPremium(containerId).then((r) => {
      if (!active) return;
      setData(r.data ?? { found: false, file: null, groups: [], nametags: [], ranktags: [] });
      setLoading(false);
    });
    return () => { active = false; };
  }, [containerId]);

  useEffect(() => {
    const v = (data?.groups.find((x) => x.name === grade)?.values ?? {}) as Record<string, unknown>;
    const f: Record<string, string> = {};
    for (const [key] of NUM_FIELDS) f[key] = v[key] != null ? String(v[key]) : '';
    for (const [key] of LIST_FIELDS) f[key] = Array.isArray(v[key]) ? (v[key] as unknown[]).join(', ') : '';
    const b: Record<string, boolean> = {};
    for (const [key] of BOOL_FIELDS) b[key] = !!v[key];
    setOrig(JSON.stringify({ f, b }));
    const pending = getDraftPayload(draftKey('grade-boutique', containerId, grade)) as { form: Record<string, string>; bools: Record<string, boolean> } | undefined;
    setForm(pending?.form ?? f);
    setBools(pending?.bools ?? b);
  }, [grade, data, containerId]);

  const buildValues = (): Record<string, unknown> => {
    const values: Record<string, unknown> = {};
    for (const [key] of NUM_FIELDS) {
      const raw = (form[key] ?? '').trim();
      if (raw !== '') values[key] = Number(raw);
    }
    for (const [key] of LIST_FIELDS) values[key] = (form[key] ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    for (const [key] of BOOL_FIELDS) values[key] = !!bools[key];
    return values;
  };

  const container = containers.find((c) => c.id === containerId);
  const dirty = useMemo(() => !readOnly && !!data?.found && JSON.stringify({ f: form, b: bools }) !== orig, [readOnly, data, form, bools, orig]);

  const persist = async (ids: string[]) => {
    const values = buildValues();
    let ok = 0, fail = 0;
    for (const cid of ids) {
      const res = await apiService.saveContainerPremiumGroup(cid, grade, values);
      if (res.data?.success) ok++; else fail++;
    }
    return { ok, fail };
  };

  const save = async () => {
    const { fail } = await persist([containerId]);
    if (fail === 0) setOrig(JSON.stringify({ f: form, b: bools }));
    return fail === 0;
  };

  useSnapshotDraft({
    kind: 'grade-boutique',
    groupId: containerId,
    identifier: grade,
    label: `Boutique · ${grade} · ${container?.name ?? containerId}`,
    dirty,
    payload: { form, bools },
    save,
  });

  const applyAll = async () => {
    if (!window.confirm(`Appliquer la config boutique du grade « ${grade} » à TOUS les containers (${containers.length}) ?`)) return;
    setApplyingAll(true); setFeedback(null);
    const { ok, fail } = await persist(containers.map((c) => c.id));
    if (fail === 0) setOrig(JSON.stringify({ f: form, b: bools }));
    setApplyingAll(false);
    setFeedback({ ok: fail === 0, msg: `Appliqué à ${ok} container(s)${fail ? `, ${fail} ignoré(s)` : ''}. Déploie via « Serveurs ».` });
  };

  if (containers.length === 0) return <p className={styles.muted}>Aucun container pour ce projet.</p>;

  return (
    <div className={styles.essBlock}>
      <div className={styles.essBar}>
        <Server size={14} />
        <select className={styles.input} value={containerId} onChange={(e) => setContainerId(e.target.value)}>
          {containers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>
      ) : !data?.found ? (
        <p className={styles.muted}>Config premium introuvable — synchronise ce container dans « Serveurs ».</p>
      ) : (
        <>
          {readOnly && <div className={styles.protectedNotice}><ShieldCheck size={16} /> Grade protégé : édition réservée aux admins.</div>}
          {LIST_FIELDS.map(([key, label, ph]) => (
            <div key={key} className={styles.essField}>
              <label className={styles.essLabel}>{label}</label>
              <input className={styles.input} value={form[key] ?? ''} disabled={readOnly} placeholder={ph} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
            </div>
          ))}

          {NUM_FIELDS.map(([key, label]) => (
            <div key={key} className={styles.essField}>
              <label className={styles.essLabel}>{label}</label>
              <input className={styles.input} type="number" value={form[key] ?? ''} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
            </div>
          ))}

          {BOOL_FIELDS.map(([key, label]) => (
            <label key={key} className={styles.essLabel} style={{ cursor: readOnly ? 'default' : 'pointer', gap: 8 }}>
              <input type="checkbox" checked={!!bools[key]} disabled={readOnly} onChange={(e) => setBools((b) => ({ ...b, [key]: e.target.checked }))} /> {label}
            </label>
          ))}

          {feedback && <span className={feedback.ok ? styles.okMsg : styles.error}>{feedback.msg}</span>}

          {!readOnly && (
          <div className={styles.essActions}>
            <button className={styles.secondaryBtn} onClick={applyAll} disabled={applyingAll}>
              {applyingAll ? <Loader2 size={14} className={styles.spin} /> : <Copy size={14} />} Appliquer à tous les containers
            </button>
          </div>
          )}
          <p className={styles.muted}>
            {dirty ? 'Modifications en attente — enregistre-les via la barre du bas.' : 'Enregistré.'} Déploie ensuite chaque container via « Serveurs ».
          </p>
        </>
      )}
    </div>
  );
}
